import React, { useState, useEffect } from 'react';
import { Search, Calendar, ChevronRight, X, Megaphone, FileText, Download, Eye, Bell } from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../Firebase';
import { mockNews, mockNotices } from '../utils/seedData';

export default function News() {
  const [activeMainTab, setActiveMainTab] = useState('news'); // 'news' or 'notices'
  const [newsList, setNewsList] = useState([]);
  const [noticesList, setNoticesList] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchVal, setSearchVal] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [activeArticle, setActiveArticle] = useState(null);
  const [activeNoticePdf, setActiveNoticePdf] = useState(null);

  const categories = ['All', 'Announcements', 'New Schemes', 'Cyber Security', 'Financial Awareness'];

  // Fetch News and Notices dynamically from Firestore
  useEffect(() => {
    const fetchNewsAndNotices = async () => {
      setLoading(true);
      try {
        // Fetch News
        const newsSnap = await getDocs(collection(db, 'news'));
        const fbNews = [];
        newsSnap.forEach((d) => fbNews.push({ id: d.id, ...d.data() }));

        const mergedNews = [...mockNews];
        if (fbNews.length > 0) {
          fbNews.forEach((fbN) => {
            const idx = mergedNews.findIndex(n => n.id === fbN.id);
            if (idx > -1) {
              mergedNews[idx] = fbN;
            } else {
              mergedNews.push(fbN);
            }
          });
        }
        setNewsList(mergedNews);

        // Fetch Notices
        const notSnap = await getDocs(collection(db, 'notices'));
        const fbNotices = [];
        notSnap.forEach((d) => fbNotices.push({ id: d.id, ...d.data() }));

        const mergedNotices = [...mockNotices];
        if (fbNotices.length > 0) {
          fbNotices.forEach((fbNot) => {
            const idx = mergedNotices.findIndex(n => n.id === fbNot.id);
            if (idx > -1) {
              mergedNotices[idx] = fbNot;
            } else {
              mergedNotices.push(fbNot);
            }
          });
        }
        setNoticesList(mergedNotices);
      } catch (error) {
        console.warn("Could not fetch news/notices from Firestore, using defaults:", error);
        setNewsList(mockNews);
        setNoticesList(mockNotices);
      } finally {
        setLoading(false);
      }
    };

    fetchNewsAndNotices();
  }, []);

  const filteredNews = newsList.filter((item) => {
    const titleMatch = (item.title || '').toLowerCase().includes(searchVal.toLowerCase());
    const summaryMatch = (item.summary || '').toLowerCase().includes(searchVal.toLowerCase());
    const contentMatch = (item.content || '').toLowerCase().includes(searchVal.toLowerCase());
    const matchesSearch = titleMatch || summaryMatch || contentMatch;
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const filteredNotices = noticesList.filter((item) => {
    const titleMatch = (item.title || '').toLowerCase().includes(searchVal.toLowerCase());
    const noMatch = (item.noticeNo || '').toLowerCase().includes(searchVal.toLowerCase());
    const descMatch = (item.description || '').toLowerCase().includes(searchVal.toLowerCase());
    const matchesSearch = titleMatch || noMatch || descMatch;
    const matchesCategory = selectedCategory === 'All' || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDownloadPdf = (notice) => {
    if (notice.pdfUrl) {
      const link = document.createElement('a');
      link.href = notice.pdfUrl;
      link.download = notice.pdfName || `${notice.title}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      setActiveNoticePdf(notice);
    }
  };

  return (
    <div className="pt-[68px] bg-[#F7F9FC]">
      {/* Page Header */}
      <section 
        className="relative overflow-hidden text-navy py-20 px-4 text-center bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/page header.png')" }}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom,rgba(0,0,0,0.01),transparent)] pointer-events-none" />
        <div className="max-w-4xl mx-auto space-y-4 relative z-10">
          <span className="text-xs font-bold text-[#B31825] uppercase tracking-widest bg-[#B31825]/5 px-3 py-1 rounded-full w-fit mx-auto block">Notice Board & Hub</span>
          <h1 className="font-montserrat font-extrabold text-3xl sm:text-4xl text-navy">News, Notices & Circular Updates</h1>
          <p className="text-slate-600 text-sm sm:text-base font-normal max-w-2xl mx-auto">
            Stay informed with our official regulatory circulars, revisions of deposit rates, cyber security advisories, and official general notices.
          </p>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-20 text-navy font-sans max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="space-y-10">
          
          {/* Main Mode Switcher: News vs Official PDF Notices */}
          <div className="flex justify-center">
            <div className="inline-flex bg-white p-1.5 rounded-2xl border border-gray-200 shadow-md">
              <button
                onClick={() => setActiveMainTab('news')}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-montserrat font-bold text-xs sm:text-sm uppercase tracking-wider transition-all duration-300 ${
                  activeMainTab === 'news'
                    ? 'bg-[#B31825] text-white shadow-md'
                    : 'text-gray-600 hover:text-navy'
                }`}
              >
                <Megaphone className="h-4 w-4" />
                <span>News & Announcements</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeMainTab === 'news' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {newsList.length}
                </span>
              </button>

              <button
                onClick={() => setActiveMainTab('notices')}
                className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-montserrat font-bold text-xs sm:text-sm uppercase tracking-wider transition-all duration-300 ${
                  activeMainTab === 'notices'
                    ? 'bg-[#052046] text-white shadow-md'
                    : 'text-gray-600 hover:text-navy'
                }`}
              >
                <FileText className="h-4 w-4" />
                <span>Official Notices (PDFs)</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${activeMainTab === 'notices' ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-600'}`}>
                  {noticesList.length}
                </span>
              </button>
            </div>
          </div>

          {/* Controls Bar */}
          <div className="flex flex-col lg:flex-row gap-6 justify-between items-center bg-white border border-gray-200 p-6 rounded-3xl shadow-sm">
            {/* Category Tabs */}
            <div className="flex bg-[#F8FAFC] p-1.5 rounded-2xl border border-gray-150 overflow-x-auto w-full lg:w-auto no-scrollbar scroll-smooth">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-5 py-2.5 rounded-xl font-montserrat font-bold text-xs whitespace-nowrap transition-all duration-300 ${
                    selectedCategory === cat ? 'bg-[#B31825] text-white shadow-sm' : 'text-gray-500 hover:text-navy'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Search Input */}
            <div className="relative w-full lg:w-80 shrink-0">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchVal}
                onChange={(e) => setSearchVal(e.target.value)}
                placeholder={activeMainTab === 'news' ? "Search articles & news..." : "Search notices & circulars..."}
                className="w-full bg-[#F8FAFC] border border-gray-200 rounded-2xl pl-10 pr-4 py-2.5 text-xs focus:outline-none focus:border-[#B31825] focus:ring-1 focus:ring-[#B31825] text-navy"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-20 bg-white rounded-3xl border border-gray-200">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-[#B31825] border-t-transparent mb-3" />
              <p className="text-xs font-semibold text-gray-500">Loading dynamic updates...</p>
            </div>
          ) : (
            <>
              {/* ================= SECTION 1: NEWS & ANNOUNCEMENTS ================= */}
              {activeMainTab === 'news' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {filteredNews.map((item) => (
                    <div 
                      key={item.id}
                      className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-8 hover:border-[#B31825] hover:shadow-2xl transition-all duration-300 flex flex-col justify-between group"
                    >
                      <div className="space-y-4">
                        {/* Category & Date badge */}
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] bg-[#B31825]/10 text-[#B31825] font-extrabold px-3 py-1 rounded-full uppercase tracking-wider">
                            {item.category || 'Announcements'}
                          </span>
                          <div className="flex items-center text-xs text-gray-400 font-poppins">
                            <Calendar className="h-4 w-4 mr-1.5" />
                            <span>{item.date}</span>
                          </div>
                        </div>

                        {/* Title */}
                        <h3 
                          className="font-montserrat font-extrabold text-base sm:text-lg text-navy leading-snug hover:text-[#B31825] transition-colors cursor-pointer" 
                          onClick={() => setActiveArticle(item)}
                        >
                          {item.isCircular && (
                            <span className="inline-flex items-center text-[9px] bg-red-100 text-red-600 font-extrabold px-2 py-0.5 rounded mr-2 uppercase tracking-wide">
                              <Megaphone className="h-3 w-3 mr-1" /> Circular
                            </span>
                          )}
                          {item.title}
                        </h3>

                        <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light line-clamp-3">
                          {item.summary || item.content}
                        </p>
                      </div>

                      <div className="pt-6 border-t border-gray-100 mt-6 flex justify-between items-center">
                        <button 
                          onClick={() => setActiveArticle(item)}
                          className="inline-flex items-center space-x-1 text-xs font-bold text-[#B31825] uppercase tracking-wider hover:underline"
                        >
                          <span>Read Full Circular</span>
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}

                  {filteredNews.length === 0 && (
                    <div className="col-span-full py-16 text-center text-gray-500 bg-white border border-gray-200 rounded-3xl p-8">
                      <Bell className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-navy">No news announcements found matching the search criteria.</p>
                    </div>
                  )}
                </div>
              )}

              {/* ================= SECTION 2: OFFICIAL PDF NOTICES ================= */}
              {activeMainTab === 'notices' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredNotices.map((notice) => (
                      <div
                        key={notice.id}
                        className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-7 hover:border-[#052046] hover:shadow-xl transition-all duration-300 flex flex-col justify-between space-y-5 group"
                      >
                        <div className="space-y-3.5">
                          <div className="flex justify-between items-start flex-wrap gap-2">
                            <span className="text-[10px] font-mono font-bold bg-[#052046] text-white px-2.5 py-1 rounded-lg">
                              {notice.noticeNo || 'SHYD/NOT/2026'}
                            </span>
                            <div className="flex items-center text-xs text-gray-400 font-poppins">
                              <Calendar className="h-3.5 w-3.5 mr-1 text-[#B31825]" />
                              <span>{notice.date}</span>
                            </div>
                          </div>

                          <div className="space-y-1">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-golden-dark block">
                              {notice.department || notice.category || 'General Notice'}
                            </span>
                            <h3 className="font-montserrat font-extrabold text-base sm:text-lg text-[#052046] leading-snug group-hover:text-[#B31825] transition-colors">
                              {notice.title}
                            </h3>
                          </div>

                          <p className="text-gray-500 text-xs sm:text-sm leading-relaxed font-light">
                            {notice.description}
                          </p>
                        </div>

                        <div className="pt-4 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
                          <span className="text-[11px] text-gray-400 flex items-center">
                            <FileText className="h-3.5 w-3.5 mr-1 text-gray-400" />
                            <span>{notice.pdfName || 'Official_Circular.pdf'}</span>
                          </span>

                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setActiveNoticePdf(notice)}
                              className="inline-flex items-center space-x-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-[#052046] text-xs font-bold uppercase tracking-wider rounded-xl transition-colors"
                            >
                              <Eye className="h-3.5 w-3.5" />
                              <span>View</span>
                            </button>
                            <button
                              onClick={() => handleDownloadPdf(notice)}
                              className="inline-flex items-center space-x-1 px-3.5 py-1.5 bg-[#052046] hover:bg-[#0c346a] text-white text-xs font-bold uppercase tracking-wider rounded-xl transition-colors shadow-md shadow-[#052046]/10"
                            >
                              <Download className="h-3.5 w-3.5" />
                              <span>Download PDF</span>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {filteredNotices.length === 0 && (
                    <div className="py-16 text-center text-gray-500 bg-white border border-gray-200 rounded-3xl p-8">
                      <FileText className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm font-semibold text-navy">No official notices found matching the criteria.</p>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* ================= ARTICLE DETAIL OVERLAY MODAL (Image 2 Replica) ================= */}
      {activeArticle && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border border-gray-200 relative text-navy">
            {/* Close X Button */}
            <button
              onClick={() => setActiveArticle(null)}
              className="absolute top-5 right-5 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white z-20 transition-all border border-white/10"
              title="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header segment with dark background */}
            <div className="bg-[#052046] text-white p-6 sm:p-8 space-y-3 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-bl-full pointer-events-none" />
              
              <span className="text-[10px] bg-[#B31825] text-white font-extrabold px-3 py-1 rounded-full uppercase tracking-wider inline-block">
                {activeArticle.category || 'Announcements'}
              </span>

              <h2 className="font-montserrat font-extrabold text-xl sm:text-2xl text-white leading-snug pr-8">
                {activeArticle.title}
              </h2>

              <div className="flex items-center text-xs text-gray-300 font-poppins pt-1">
                <Calendar className="h-4 w-4 mr-1.5 text-golden" />
                <span>Published on {activeArticle.date}</span>
              </div>
            </div>

            {/* Content segment with highlight quote box */}
            <div className="p-6 sm:p-8 space-y-5 max-h-[360px] overflow-y-auto font-sans">
              {activeArticle.summary && (
                <div className="text-xs sm:text-sm text-gray-700 font-semibold leading-relaxed border-l-4 border-[#B31825] pl-4 bg-[#F8FAFC] py-3 rounded-r-xl border border-gray-100">
                  {activeArticle.summary}
                </div>
              )}

              <p className="text-xs sm:text-sm text-gray-600 leading-relaxed whitespace-pre-line font-light">
                {activeArticle.content || activeArticle.summary}
              </p>
            </div>

            {/* Footer segment with Close Notice button */}
            <div className="p-5 bg-[#F8FAFC] border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setActiveArticle(null)}
                className="px-6 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white text-xs font-bold rounded-xl uppercase tracking-wider shadow-md transition-all"
              >
                Close Notice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= NOTICE PDF VIEWER MODAL ================= */}
      {activeNoticePdf && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
          <div className="bg-white w-full max-w-2xl rounded-3xl overflow-hidden shadow-2xl border border-gray-200 relative text-navy">
            {/* Close button */}
            <button
              onClick={() => setActiveNoticePdf(null)}
              className="absolute top-5 right-5 p-2 rounded-xl bg-white/10 hover:bg-white/20 text-white z-20 transition-all border border-white/10"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Header */}
            <div className="bg-[#052046] text-white p-6 sm:p-8 space-y-2 relative">
              <span className="text-[10px] bg-golden text-navy font-bold px-2.5 py-0.5 rounded uppercase font-mono">
                {activeNoticePdf.noticeNo || 'OFFICIAL CIRCULAR'}
              </span>
              <h2 className="font-montserrat font-extrabold text-lg sm:text-xl text-white pr-8">
                {activeNoticePdf.title}
              </h2>
              <div className="flex items-center text-xs text-gray-300 space-x-4">
                <span>Date: {activeNoticePdf.date}</span>
                <span>•</span>
                <span>Department: {activeNoticePdf.department || 'Administration'}</span>
              </div>
            </div>

            {/* Document Content View */}
            <div className="p-6 sm:p-8 space-y-4 max-h-[380px] overflow-y-auto">
              <div className="bg-[#F8FAFC] border border-gray-200 rounded-2xl p-5 space-y-3">
                <div className="flex items-center space-x-3 text-[#052046]">
                  <FileText className="h-8 w-8 text-[#B31825]" />
                  <div>
                    <h4 className="font-bold text-sm text-navy">{activeNoticePdf.pdfName || 'Official_Notice.pdf'}</h4>
                    <p className="text-xs text-gray-400">Official Sahyadri Urban Credit Society Document</p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-3">
                  <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider block mb-1">Notice Description:</span>
                  <p className="text-xs text-gray-700 leading-relaxed whitespace-pre-line">
                    {activeNoticePdf.description}
                  </p>
                </div>

                {activeNoticePdf.pdfUrl && (
                  <div className="pt-2">
                    <iframe
                      src={activeNoticePdf.pdfUrl}
                      title="PDF Preview"
                      className="w-full h-48 rounded-xl border border-gray-200"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="p-4 bg-[#F8FAFC] border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => setActiveNoticePdf(null)}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs font-bold rounded-xl uppercase transition-colors"
              >
                Close
              </button>

              {activeNoticePdf.pdfUrl ? (
                <a
                  href={activeNoticePdf.pdfUrl}
                  download={activeNoticePdf.pdfName || 'Notice.pdf'}
                  className="px-5 py-2.5 bg-[#B31825] hover:bg-[#8e131d] text-white text-xs font-bold rounded-xl uppercase flex items-center space-x-1.5 shadow-md"
                >
                  <Download className="h-4 w-4" />
                  <span>Download Document</span>
                </a>
              ) : (
                <button
                  onClick={() => alert("This sample circular is a verified digital notice in the society record.")}
                  className="px-5 py-2.5 bg-[#052046] hover:bg-[#0c346a] text-white text-xs font-bold rounded-xl uppercase flex items-center space-x-1.5 shadow-md"
                >
                  <FileText className="h-4 w-4" />
                  <span>Verified Notice</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
